import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = ["aria-expanded", "aria-controls", "disabled", "aria-disabled"]
const _hoisted_2 = { class: "dropdown-item-after" }

import {
  computed, inject, onBeforeUnmount, ref, useId, useSlots
} from 'vue';
import { useDropdownItem } from '@components/RcDropdown/useDropdownItem';
import { RcIcon } from '@components/RcIcon';
import { DropdownContext, DropdownSubmenu, defaultContext } from './types';


export default /*@__PURE__*/_defineComponent({
  __name: 'RcDropdownSubmenu',
  props: {
    side: { default: 'right' },
    disabled: { type: Boolean, default: false }
  },
  setup(__props: any) {

/**
 * An item that opens a submenu beside its menu. Used in conjunction with RcDropdown, one level deep:
 * one inside a submenu isn't drawn, and says so in the console during development.
 *
 * It opens on click, hover, Enter, Space or the arrow toward its side, and the submenu closes with
 * Escape or the arrow back, returning the focus to this item.
 *
 * Example:
 *
 *  <rc-dropdown-submenu side="left">
 *    Group By
 *    <template #after>
 *      {{ groupLabel }}
 *    </template>
 *    <template #submenu>
 *      <rc-dropdown-item-radio
 *        v-for="option in options"
 *        :key="option.id"
 *        :checked="option.id === groupBy"
 *        @click="groupBy = option.id"
 *      >
 *        {{ option.label }}
 *      </rc-dropdown-item-radio>
 *    </template>
 *  </rc-dropdown-submenu>
 */
const props = __props;

const {
  submenuId, activeSubmenu, registerSubmenu, openSubmenu, hoverSubmenu, cancelSubmenuSwitch, inSubmenu
} = inject<DropdownContext>('dropdownContext') || defaultContext;

if (inSubmenu && process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.warn('RcDropdownSubmenu: submenus are one level deep, so one inside a submenu is not drawn');
}

const { handleKeydown, scrollIntoView } = useDropdownItem();

const slots = useSlots();

const rowId = `rc-dropdown-submenu-item-${ useId() }`;

const labelId = `${ rowId }-label`;

const row = ref<HTMLElement | null>(null);

const submenu: DropdownSubmenu = {
  get side() {
    return props.side;
  },
  row:    () => row.value,
  labelId,
  render: () => slots.submenu?.(),
};

onBeforeUnmount(registerSubmenu(submenu));

const expanded = computed(() => activeSubmenu.value === submenu);

const open = (focusFirst: boolean) => {
  if (!props.disabled) {
    openSubmenu(submenu, focusFirst);
  }
};

const onKeydown = (e: KeyboardEvent) => {
  if (e.altKey || e.ctrlKey || e.metaKey) {
    return;
  }

  const opens = e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight' || (props.side === 'left' && e.key === 'ArrowLeft');

  if (opens) {
    e.preventDefault();
    e.stopPropagation();
    open(true);
  }
};

return (_ctx: any,_cache: any) => {
  return (!_unref(inSubmenu))
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        id: rowId,
        ref_key: "row",
        ref: row,
        "dropdown-menu-item": "",
        tabindex: "-1",
        role: "menuitem",
        "aria-haspopup": "menu",
        "aria-expanded": expanded.value,
        "aria-controls": expanded.value ? _unref(submenuId) : undefined,
        class: _normalizeClass({ expanded: expanded.value }),
        disabled: __props.disabled || null,
        "aria-disabled": __props.disabled || false,
        onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (open(false)), ["stop"])),
        onKeydown: [
          onKeydown,
          _cache[1] || (_cache[1] = _withKeys(_withModifiers(
//@ts-ignore
(...args) => (_unref(handleKeydown) && _unref(handleKeydown)(...args)), ["prevent","stop"]), ["up","down"]))
        ],
        onMousedown: _withModifiers(() => {/* As RcDropdownItem: a click doesn't take the focus */}, ["prevent"]),
        onMouseenter: _cache[2] || (_cache[2] = ($event: any) => (!__props.disabled && _unref(hoverSubmenu)(submenu))),
        onMouseleave: _cache[3] || (_cache[3] = ($event: any) => (_unref(cancelSubmenuSwitch)())),
        onFocusin: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(scrollIntoView) && _unref(scrollIntoView)(...args)))
      }, [
        _renderSlot(_ctx.$slots, "before"),
        _cache[6] || (_cache[6] = _createTextVNode()),
        _createElementVNode("span", { id: labelId }, [
          _renderSlot(_ctx.$slots, "default")
        ]),
        _cache[7] || (_cache[7] = _createTextVNode()),
        _createElementVNode("span", _hoisted_2, [
          _renderSlot(_ctx.$slots, "after"),
          _cache[5] || (_cache[5] = _createTextVNode()),
          _createVNode(_unref(RcIcon), {
            type: "chevron-right",
            size: "inherit"
          })
        ])
      ], 42, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})